import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Building2, Bed, Users, TrendingUp } from 'lucide-react';
import Layout from '@/components/Layout';
import FloorSection from '@/components/FloorSection';
import BedDrawer from '@/components/BedDrawer';
import TransferModal from '@/components/TransferModal';
import EditRoomModal from '@/components/EditRoomModal';
import VacateModal from '@/components/VacateModal';
import AddFloorModal from '@/components/AddFloorModal';
import AddRoomModal from '@/components/AddRoomModal';

export default function PGVisualLayout() {
  const { pgId } = useParams();
  const navigate = useNavigate();
  const [pg, setPg] = useState(null);
  const [floors, setFloors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [beds, setBeds] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedBedData, setSelectedBedData] = useState(null);
  
  // Drag state
  const [dragTargetBedId, setDragTargetBedId] = useState(null);
  
  // Transfer modal state
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferData, setTransferData] = useState(null);
  const [transferLoading, setTransferLoading] = useState(false);
  
  // Edit room modal state
  const [editRoomModalOpen, setEditRoomModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomSaveLoading, setRoomSaveLoading] = useState(false);

  // Vacate modal state
  const [vacateModalOpen, setVacateModalOpen] = useState(false);
  const [vacateTenant, setVacateTenant] = useState(null);
  const [vacateLoading, setVacateLoading] = useState(false);
  const [addFloorModalOpen, setAddFloorModalOpen] = useState(false);
  const [addRoomModalOpen, setAddRoomModalOpen] = useState(false);
  const [selectedFloorForRoom, setSelectedFloorForRoom] = useState(null);
  const [floorSaveLoading, setFloorSaveLoading] = useState(false);
  const [roomCreateLoading, setRoomCreateLoading] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, [pgId]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [pgRes, floorsRes, tenantsRes, statsRes] = await Promise.all([
        api.getPG(pgId),
        api.getFloors(pgId),
        api.getTenants(pgId),
        api.getDashboard(pgId)
      ]);

      setPg(pgRes.data);
      const floorsData = floorsRes.data;
      setFloors(floorsData);
      setTenants(tenantsRes.data);
      setStats(statsRes.data);

      // Fetch rooms for all floors
      const roomsPromises = floorsData.map(floor => api.getRooms(floor.id));
      const roomsResults = await Promise.all(roomsPromises);
      const roomsData = roomsResults.flatMap(res => res.data);
      setRooms(roomsData);

      // Fetch beds for all rooms
      const bedsPromises = roomsData.map(room => api.getBeds(room.id));
      const bedsResults = await Promise.all(bedsPromises);
      const bedsData = bedsResults.flatMap(res => res.data);
      setBeds(bedsData);
    } catch (error) {
      toast.error('Failed to load PG data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBedClick = (bed, room, floor, tenant) => {
    setSelectedBedData({ bed, room, floor, tenant });
    setDrawerOpen(true);
  };

  const handleDragStart = (bedId) => {
    setDragTargetBedId(bedId);
  };

  const handleDrop = (dropData) => {
    setTransferData(dropData);
    setTransferModalOpen(true);
    setDragTargetBedId(null);
  };

  const handleConfirmTransfer = async () => {
    if (!transferData) return;
    
    setTransferLoading(true);
    try {
      await api.transferTenant(transferData.tenantId, transferData.newBedId);
      toast.success('Tenant transferred successfully!');
      setTransferModalOpen(false);
      setTransferData(null);
      setDrawerOpen(false);
      await fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer tenant');
    } finally {
      setTransferLoading(false);
    }
  };

  const handleTransferFromDrawer = (tenant) => {
    setDrawerOpen(false);
    toast.info('Drag the tenant to a vacant bed to transfer');
  };

  const handleVacate = (tenant) => {
    setVacateTenant(tenant);
    setVacateModalOpen(true);
  };

  const handleConfirmVacate = async (vacateDate, deductions) => {
    if (!vacateTenant) return;
    
    setVacateLoading(true);
    try {
      await api.vacateTenant(vacateTenant.id, vacateDate, deductions);
      toast.success('Tenant vacated successfully!');
      setVacateModalOpen(false);
      setVacateTenant(null);
      setDrawerOpen(false);
      await fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to vacate tenant');
    } finally {
      setVacateLoading(false);
    }
  };

  const handleEditRoom = (room) => {
    setSelectedRoom(room);
    setEditRoomModalOpen(true);
  };

  const handleSaveRoom = async (formData) => {
    setRoomSaveLoading(true);
    try {
      await api.updateRoom(selectedRoom.id, formData);
      toast.success('Room updated successfully!');
      setEditRoomModalOpen(false);
      await fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update room');
    } finally {
      setRoomSaveLoading(false);
    }
  };

  const handleDeleteRoom = async (room) => {
    if (!window.confirm(`Are you sure you want to delete Room ${room.roomNumber}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await api.deleteRoom(room.id);
      toast.success('Room deleted successfully!');
      await fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete room');
    }
  };

  const handleOpenAddRoom = (floor) => {
    setSelectedFloorForRoom(floor);
    setAddRoomModalOpen(true);
  };

  const handleAddFloor = async (formData) => {
    setFloorSaveLoading(true);
    try {
      await api.createFloor({
        pgId: Number(pgId),
        floorNumber: Number(formData.floorNumber),
        floorName: formData.floorName
      });
      toast.success('Floor added successfully!');
      setAddFloorModalOpen(false);
      await fetchAllData();
    } catch (error) {
      toast.error(error.message || 'Failed to add floor');
    } finally {
      setFloorSaveLoading(false);
    }
  };

  const handleDeleteFloor = async (floor) => {
    if (!window.confirm(`Are you sure you want to delete ${floor.floorName}? This will also remove all vacant rooms and beds on that floor.`)) {
      return;
    }

    try {
      await api.deleteFloor(floor.id);
      toast.success('Floor deleted successfully!');
      await fetchAllData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete floor');
    }
  };

  const handleAddRoom = async (formData) => {
    setRoomCreateLoading(true);
    try {
      await api.createRoom(formData);
      toast.success('Room added successfully!');
      setAddRoomModalOpen(false);
      setSelectedFloorForRoom(null);
      await fetchAllData();
    } catch (error) {
      toast.error(error.message || 'Failed to add room');
    } finally {
      setRoomCreateLoading(false);
    }
  };

  const nextFloorNumber = floors.length > 0
    ? Math.max(...floors.map((floor) => floor.floorNumber || 0)) + 1
    : 1;

  const selectedFloorRoomCount = selectedFloorForRoom
    ? rooms.filter((room) => room.floorId === selectedFloorForRoom.id).length
    : 0;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (!pg) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-600">PG not found</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <Button
            onClick={() => navigate('/pgs')}
            variant="ghost"
            className="mb-4"
            data-testid="back-to-pgs"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to PG List
          </Button>

          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center">
                <Building2 className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900 font-heading">{pg.name}</h1>
                <p className="text-slate-600 mt-1">{pg.address}</p>
              </div>
            </div>
            <Button
              type="button"
              className="rounded-xl"
              onClick={() => setAddFloorModalOpen(true)}
              data-testid="open-add-floor"
            >
              Add Floor
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Bed className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-700">Total Beds</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_beds}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Bed className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-700">Vacant Beds</p>
                  <p className="text-2xl font-bold text-green-600">{stats.vacant_beds}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-700">Occupied</p>
                  <p className="text-2xl font-bold text-red-600">{stats.occupied_beds}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-700">Occupancy</p>
                  <p className="text-2xl font-bold text-primary">{stats.occupancy_rate}%</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-4">
          <p className="text-sm text-slate-700">
            <span className="font-bold">💡 Tip:</span> Click any bed to view details. 
            Drag occupied beds (blue) to vacant beds (green) to transfer tenants instantly!
          </p>
        </div>

        {/* Floor Layout */}
        <div className="space-y-6">
          {floors.length === 0 ? (
            <div className="bg-white rounded-xl border-2 border-slate-200 p-12 text-center">
              <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No floors configured</h3>
              <p className="text-slate-600 mb-4">Add your first floor to start building the visual layout.</p>
              <Button onClick={() => setAddFloorModalOpen(true)}>
                Add First Floor
              </Button>
            </div>
          ) : (
            floors.map((floor) => (
              <FloorSection
                key={floor.id}
                floor={floor}
                rooms={rooms}
                beds={beds}
                tenants={tenants}
                onBedClick={handleBedClick}
                onDragStart={handleDragStart}
                onDrop={handleDrop}
                dragTargetBedId={dragTargetBedId}
                onEditRoom={handleEditRoom}
                onDeleteRoom={handleDeleteRoom}
                onAddRoom={handleOpenAddRoom}
                onDeleteFloor={handleDeleteFloor}
              />
            ))
          )}
        </div>
      </div>

      {/* Bed Drawer */}
      <BedDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        bedData={selectedBedData}
        onTransfer={handleTransferFromDrawer}
        onVacate={handleVacate}
      />

      {/* Transfer Modal */}
      <TransferModal
        isOpen={transferModalOpen}
        onClose={() => {
          setTransferModalOpen(false);
          setTransferData(null);
        }}
        transferData={transferData}
        onConfirm={handleConfirmTransfer}
        loading={transferLoading}
      />
      
      {/* Edit Room Modal */}
      <EditRoomModal
        isOpen={editRoomModalOpen}
        onClose={() => setEditRoomModalOpen(false)}
        room={selectedRoom}
        onSave={handleSaveRoom}
        loading={roomSaveLoading}
      />

      {/* Vacate Modal */}
      <VacateModal
        isOpen={vacateModalOpen}
        onClose={() => {
          setVacateModalOpen(false);
          setVacateTenant(null);
        }}
        tenant={vacateTenant}
        onConfirm={handleConfirmVacate}
        loading={vacateLoading}
      />

      <AddFloorModal
        isOpen={addFloorModalOpen}
        onClose={() => setAddFloorModalOpen(false)}
        onSave={handleAddFloor}
        loading={floorSaveLoading}
        nextFloorNumber={nextFloorNumber}
      />

      <AddRoomModal
        isOpen={addRoomModalOpen}
        onClose={() => {
          setAddRoomModalOpen(false);
          setSelectedFloorForRoom(null);
        }}
        onSave={handleAddRoom}
        loading={roomCreateLoading}
        floor={selectedFloorForRoom}
        defaultRent={pg?.defaultRent}
        roomCount={selectedFloorRoomCount}
      />
    </Layout>
  );
}